import React from "react";
import {CustomPopper} from "~/components/customPopper/CustomPopper.tsx";
import type {ContractDocument} from "../contractExplorerTypes.ts";
import type {DocumentRow} from "../contractExplorerFormat.ts";
import {formatNumber, MISSING, typeLabel} from "../contractExplorerFormat.ts";
import {SourceCite} from "./SourceCite.tsx";

type Props = {tiers: DocumentRow[]; documents: Record<string, ContractDocument>};

const band = (row: Record<string, unknown>): string => {
    const min = row.min_value, max = row.max_value;
    if (typeof max !== 'number') return `${formatNumber(min)} and up`;
    return `${typeof min === 'number' ? formatNumber(min) : '0'} – ${formatNumber(max)}`;
};

const price = (row: Record<string, unknown>): string => {
    if (typeof row.unit_price === 'number') return formatNumber(row.unit_price);
    if (typeof row.rate_pct === 'number') return `${formatNumber(row.rate_pct)} %`;
    return MISSING;
};

/** A line's price bands, as its document states them: a count that opens the bands. */
export const PriceTiers: React.FC<Props> = ({tiers, documents}) => {
    const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
    if (!tiers.length) return null;
    const basis = typeLabel(String(tiers[0].row.band_basis ?? ''));
    return <>
        <button type="button" className="ci-link"
                onClick={event => {
                    event.stopPropagation();
                    setAnchor(anchor ? null : event.currentTarget);
                }}>
            {tiers.length} {tiers.length === 1 ? 'tier' : 'tiers'}
        </button>
        <CustomPopper open={anchor !== null} anchorEl={anchor} placement="bottom-end" disablePortal={false}
                      onClose={() => setAnchor(null)} paperSx={{p: 1.5, maxWidth: 380}}>
            <div className="ci-rivals">
                <span className="ci-kv-l">Price by {basis === MISSING ? 'band' : basis.toLowerCase()}</span>
                {tiers.map(({row, document}) => (
                    <div key={String(row.tier_id ?? `${document.document_id}:${band(row)}`)} className="ci-tier">
                        <span>{band(row)}</span>
                        <span className="ci-mono">{price(row)}</span>
                        <SourceCite documentId={document.document_id} documents={documents}/>
                    </div>
                ))}
            </div>
        </CustomPopper>
    </>;
};
